import { useMemo } from 'react'

import {
  CrownOutlined,
  DownOutlined,
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  UserSwitchOutlined
} from '@ant-design/icons'
import { Dropdown, Flex, MenuProps, Typography } from 'antd'

import { isLockedApi } from '@eastcoal/api/auth/auth/isLocked'
import { logoutApi } from '@eastcoal/api/auth/auth/logout'
import { switchMemberApi } from '@eastcoal/api/auth/auth/switchMember'

import { system } from '@eastcoal/common/config/system'
import { useFeedback } from '@eastcoal/common/hooks/useFeedback'
import { browserOpen, browserRedirect } from '@eastcoal/common/utils/browser'
import { pick } from '@eastcoal/common/utils/object'
import { gotoLogin } from '@eastcoal/common/utils/route'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'
import { NullIcon } from '@eastcoal/icon/NullIcon'
import { RouterLink } from '@eastcoal/router/components/Link'
import { useNavigate } from '@eastcoal/router/exports/react-router'

import { DeviceType } from '../../enums/DeviceType'
import { MemberType } from '../../enums/MemberType'
import { MemberApproveStatusLink } from '../MemberApproveStatusLink'
import { UserAvator } from '../UserAvatar'

import { GlobalAvatarProps } from './interface'

export const GlobalAvatar = ({
  className,
  style,
  showMallCenter,
  showMemberCenter
}: GlobalAvatarProps) => {
  const navigate = useNavigate()
  const { modal } = useFeedback()
  const { logined, user, members, member } = useAuthStore(state =>
    pick(state, ['logined', 'user', 'members', 'member'])
  )

  const items = useMemo(() => {
    const menuItems: MenuProps['items'] = [
      {
        key: 'userInfo',
        label: `${user.phone}`,
        icon: <UserAvator size='small' />,
        style: {
          marginBlockEnd: 10
        }
      },
      {
        key: 'switchMember',
        label: '切换身份',
        icon: <UserSwitchOutlined />,
        expandIcon: <NullIcon />,
        children: members.map(item => {
          const isCurrentMember = item.memberId === member.id
          return {
            key: `${item.memberId}`,
            label: `${item.memberName}`,
            disabled: isCurrentMember,
            async onClick() {
              await switchMemberApi({
                profileId: item.profileId,
                device: DeviceType.PC
              })
              browserRedirect('/')
            }
          }
        })
      }
    ]

    if (showMallCenter) {
      menuItems.push(
        {
          key: 'userCenter',
          label: '个人中心',
          icon: <UserOutlined />,
          async onClick() {
            navigate('/userCenter')
          }
        },
        {
          key: 'mallCenter',
          label: '前往商城首页',
          icon: <HomeOutlined />,
          onClick: () => {
            browserOpen(system.router.mallBaseUrl, '_blank')
          }
        }
      )
    }

    if (showMemberCenter) {
      menuItems.push({
        key: 'memberCenter',
        label: '前往会员中心',
        icon: <CrownOutlined />,
        onClick: () => {
          browserOpen(system.router.memberBaseUrl, '_blank')
        }
      })
    }

    menuItems.push(
      {
        type: 'divider'
      },
      {
        key: 'logout',
        label: '退出',
        icon: <LogoutOutlined />,
        async onClick() {
          const doLogout = async () => {
            try {
              // 忽略错误
              await logoutApi(undefined, { silence: true })
            } finally {
              gotoLogin()
            }
          }
          let isLocked = false
          try {
            isLocked = await isLockedApi(
              { device: DeviceType.PC },
              { silence: true }
            )
          } catch {
            // ignore
          }
          if (isLocked) {
            modal.confirm({
              title: '账号已锁定',
              content:
                '您的账号正在参与竞价，退出登录可能会造成出价显示和提交中断，请确认是否继续退出？',
              onOk: doLogout
            })
          } else {
            await doLogout()
          }
        }
      }
    )

    return menuItems
  }, [
    user.phone,
    members,
    showMallCenter,
    showMemberCenter,
    member.id,
    navigate,
    modal
  ])

  const userName =
    member.type === MemberType.PERSON
      ? `${user.name ?? user.phone}（个人会员）`
      : `${user.name}-${member.name}`

  return logined ? (
    <Flex align='center' gap={4}>
      <MemberApproveStatusLink approveStatus={member.approveStatus} />
      <Dropdown menu={{ items }}>
        <Typography.Link className={className} style={style}>
          <Flex gap={4} vertical={false}>
            <span>{userName}</span>
            <DownOutlined />
          </Flex>
        </Typography.Link>
      </Dropdown>
    </Flex>
  ) : (
    <RouterLink
      hoverUnderline={false}
      newtab={false}
      to=''
      type='text'
      onClick={gotoLogin}
    >
      登录/注册
    </RouterLink>
  )
}
